import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, Lock, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  en: {
    title: 'Set a new password',
    subtitle: 'Choose something at least 8 characters long.',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    submit: 'Update password',
    mismatch: 'Passwords do not match.',
    tooShort: 'Password must be at least 8 characters.',
    success: 'Password updated. Sign in with your new password.',
    missingToken: 'Reset link is missing or invalid.',
    backToLogin: 'Back to sign in',
  },
  bg: {
    title: 'Задай нова парола',
    subtitle: 'Избери минимум 8 символа.',
    newPassword: 'Нова парола',
    confirmPassword: 'Потвърди паролата',
    submit: 'Обнови паролата',
    mismatch: 'Паролите не съвпадат.',
    tooShort: 'Паролата трябва да е поне 8 символа.',
    success: 'Паролата е обновена. Влез с новата парола.',
    missingToken: 'Линкът за смяна е невалиден или липсва.',
    backToLogin: 'Обратно към вход',
  },
  es: {
    title: 'Establece una nueva contraseña',
    subtitle: 'Elige al menos 8 caracteres.',
    newPassword: 'Nueva contraseña',
    confirmPassword: 'Confirmar contraseña',
    submit: 'Actualizar contraseña',
    mismatch: 'Las contraseñas no coinciden.',
    tooShort: 'La contraseña debe tener al menos 8 caracteres.',
    success: 'Contraseña actualizada. Inicia sesión con la nueva.',
    missingToken: 'El enlace no es válido o falta.',
    backToLogin: 'Volver a iniciar sesión',
  },
  de: {
    title: 'Neues Passwort setzen',
    subtitle: 'Mindestens 8 Zeichen wählen.',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort bestätigen',
    submit: 'Passwort aktualisieren',
    mismatch: 'Passwörter stimmen nicht überein.',
    tooShort: 'Das Passwort muss mindestens 8 Zeichen haben.',
    success: 'Passwort aktualisiert. Melde dich mit dem neuen an.',
    missingToken: 'Der Link ist ungültig oder fehlt.',
    backToLogin: 'Zurück zur Anmeldung',
  },
};

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <div className="w-full max-w-md rounded-2xl border border-destructive/40 bg-card p-8 text-center" data-testid="reset-missing-token">
          <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{c.missingToken}</p>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-primary hover:underline text-sm">
            <ArrowLeft className="w-4 h-4" />
            {c.backToLogin}
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error(c.tooShort);
      return;
    }
    if (pwd !== confirmPwd) {
      toast.error(c.mismatch);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pwd });
      toast.success(c.success);
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed. Try requesting a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-lg"
        data-testid="reset-password-card"
      >
        <h1 className="font-heading text-2xl font-bold mb-2">{c.title}</h1>
        <p className="text-muted-foreground text-sm mb-6">{c.subtitle}</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" htmlFor="rp-new">
              {c.newPassword}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="rp-new"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                minLength={8}
                className="pl-9 h-11"
                data-testid="reset-new-password"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" htmlFor="rp-confirm">
              {c.confirmPassword}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="rp-confirm"
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                minLength={8}
                className="pl-9 h-11"
                data-testid="reset-confirm-password"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading || !pwd || !confirmPwd}
            className="w-full h-11 rounded-full"
            data-testid="reset-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : c.submit}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
